import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Filter, X } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { SelectionOption, SelectionList } from '@/shared/ui/selection-list'

export interface CatalogFloatingFiltersProps {
  currentCategory?: string
  categories?: Array<{ value: string; label: string }>
  filtersRef: React.RefObject<HTMLDivElement | null>
}

/**
 * Плавающая кнопка фильтров каталога
 * 
 * Показывается когда пользователь проскроллил мимо основных фильтров.
 */
export function CatalogFloatingFilters({
  currentCategory,
  categories = [],
  filtersRef,
}: CatalogFloatingFiltersProps) {
  const { hapticFeedback } = useTelegram()
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  useEffect(() => {
    const filtersElement = filtersRef.current
    if (!filtersElement) return

    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(!entries[0].isIntersecting)
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px',
      }
    )

    observer.observe(filtersElement)
    return () => observer.disconnect()
  }, [filtersRef])

  const handleCategoryChange = (category?: string) => {
    navigate({
      // @ts-ignore
      search: (prev: any) => ({
        ...prev,
        category: category || undefined,
      }),
    })
    setIsFiltersOpen(false)
  }

  const hasActiveFilters = !!currentCategory

  if (!isVisible) return null

  return (
    <>
      <button
        onClick={() => {
          hapticFeedback.impactOccurred('medium')
          setIsFiltersOpen(true)
        }}
        className={cn(
          'fixed right-4 bottom-24 z-50',
          'w-14 h-14 rounded-full',
          'flex items-center justify-center',
          'active:scale-[0.95] transition-all duration-200',
          hasActiveFilters
            ? 'bg-primary text-white shadow-lg'
            : 'bg-white dark:bg-dark-elevated text-primary border border-gray-200/50 dark:border-dark-border shadow-lg'
        )}
        style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="Фильтры"
      >
        <Filter className="h-5 w-5" strokeWidth={2} />
        {hasActiveFilters && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-white dark:bg-dark-elevated rounded-full shadow-sm flex items-center justify-center">
            <span className="w-2 h-2 bg-primary rounded-full" />
          </span>
        )}
      </button>

      <BottomSheet isOpen={isFiltersOpen} onClose={() => setIsFiltersOpen(false)} height={55}>
        <div className="px-4 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-ink dark:text-white">Фильтры</h3>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => handleCategoryChange(undefined)}
                className="text-sm text-primary dark:text-primary-light flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                Сбросить
              </button>
            )}
          </div>

          {categories.length > 0 && (
            <SelectionList title="Категория">
              <SelectionOption
                selected={!currentCategory}
                onClick={() => handleCategoryChange(undefined)}
                label="Все категории"
              />
              {categories.map((category) => (
                <SelectionOption
                  key={category.value}
                  selected={currentCategory === category.value}
                  onClick={() => handleCategoryChange(category.value)}
                  label={category.label}
                />
              ))}
            </SelectionList>
          )}
        </div>
      </BottomSheet>
    </>
  )
}
